import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, KeyRound, Copy, RefreshCw } from 'lucide-react';

// Staff roles that can enable 2FA (mirrors backend STAFF_ROLES)
const STAFF_ROLES = ['Admin', 'Manager', 'Support', 'Courier', 'ContentCreator'];

export default function TwoFactorSettings({ user }) {
  const [status, setStatus] = useState(null); // { enabled, recovery_codes_remaining }
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('idle'); // idle | setup | confirm | showCodes | disabling
  const [qr, setQr] = useState(null);
  const [uri, setUri] = useState('');
  const [rawSecret, setRawSecret] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  const isStaff = STAFF_ROLES.includes(user?.role);

  const fetchStatus = async () => {
    if (!isStaff) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/auth/2fa/status`, { headers: getAuthHeaders() });
      setStatus(res.data);
    } catch (e) {
      // silent — non-staff or endpoint missing
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []); // fetch once on mount

  if (!isStaff) return null;
  if (loading) return null;

  const startSetup = async () => {
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`, {}, { headers: getAuthHeaders() });
      setQr(res.data.qr_data_url);
      setUri(res.data.otpauth_uri);
      setRawSecret(res.data.secret);
      setStep('confirm');
      setCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Не удалось начать настройку');
    }
  };

  const confirmEnable = async () => {
    if (!code.trim()) return;
    try {
      const res = await axios.post(`${API}/auth/2fa/enable`,
        { code: code.trim() }, { headers: getAuthHeaders() });
      setRecoveryCodes(res.data.recovery_codes || []);
      setStep('showCodes');
      setCode('');
      fetchStatus();
      toast.success('2FA включена');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Неверный код');
    }
  };

  const disable2FA = async () => {
    if (!password || !code.trim()) return;
    try {
      await axios.post(`${API}/auth/2fa/disable`,
        { password, code: code.trim() }, { headers: getAuthHeaders() });
      setStep('idle');
      setPassword(''); setCode('');
      fetchStatus();
      toast.success('2FA отключена');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка отключения');
    }
  };

  const regenerateCodes = async () => {
    const c = window.prompt('Введите код из Authenticator для подтверждения:');
    if (!c || !c.trim()) return;
    try {
      const res = await axios.post(`${API}/auth/2fa/recovery-codes/regenerate`,
        { code: c.trim() }, { headers: getAuthHeaders() });
      setRecoveryCodes(res.data.recovery_codes || []);
      setStep('showCodes');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка');
    }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    toast.success('Коды скопированы');
  };

  return (
    <Card className="mt-6" data-testid="2fa-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {status?.enabled
            ? <ShieldCheck className="w-5 h-5 text-emerald-600" />
            : <ShieldOff className="w-5 h-5 text-gray-400" />}
          Двухфакторная аутентификация (2FA)
        </CardTitle>
        <CardDescription>
          {status?.enabled
            ? `Включена. Кодов восстановления осталось: ${status.recovery_codes_remaining}`
            : 'Дополнительная защита учётной записи через Google Authenticator, Authy или 1Password.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* IDLE */}
        {step === 'idle' && !status?.enabled && (
          <Button onClick={startSetup} data-testid="2fa-start-setup" className="bg-emerald-700 hover:bg-emerald-800 text-white">
            <KeyRound className="w-4 h-4 mr-2" /> Включить 2FA
          </Button>
        )}
        {step === 'idle' && status?.enabled && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={regenerateCodes} data-testid="2fa-regen-codes">
              <RefreshCw className="w-4 h-4 mr-2" /> Перегенерировать коды
            </Button>
            <Button variant="destructive" onClick={() => setStep('disabling')} data-testid="2fa-disable-button">
              <ShieldOff className="w-4 h-4 mr-2" /> Отключить
            </Button>
          </div>
        )}

        {/* SETUP CONFIRM */}
        {step === 'confirm' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              1. Отсканируйте QR-код в приложении Authenticator (или введите секрет вручную).
            </p>
            {qr && <img src={qr} alt="QR" className="w-56 h-56 border rounded" data-testid="2fa-qr-img" />}
            <p className="text-xs text-gray-500 break-all">
              Секрет для ручного ввода: <code className="bg-gray-100 px-1">{rawSecret}</code>
            </p>
            <p className="text-sm text-gray-700 mt-4">2. Введите первый 6-значный код из приложения:</p>
            <div className="flex gap-2">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={6}
                inputMode="numeric"
                placeholder="000000"
                className="tracking-widest text-center max-w-[160px]"
                data-testid="2fa-confirm-code"
              />
              <Button onClick={confirmEnable} disabled={code.length !== 6} data-testid="2fa-confirm-btn"
                      className="bg-emerald-700 hover:bg-emerald-800 text-white">
                Подтвердить
              </Button>
              <Button variant="outline" onClick={() => { setStep('idle'); setCode(''); }}>
                Отмена
              </Button>
            </div>
          </div>
        )}

        {/* SHOW BACKUP CODES */}
        {step === 'showCodes' && (
          <div className="space-y-3">
            <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              ⚠️ Сохраните эти <b>одноразовые</b> коды восстановления. Каждый работает один раз — используйте если потеряете доступ к Authenticator.
            </div>
            <div className="grid grid-cols-2 gap-2 font-mono text-sm" data-testid="2fa-recovery-list">
              {recoveryCodes.map((c, i) => (
                <div key={i} className="bg-gray-100 rounded px-2 py-1">{c}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copyCodes} data-testid="2fa-copy-codes">
                <Copy className="w-4 h-4 mr-2" /> Скопировать все
              </Button>
              <Button onClick={() => setStep('idle')} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="2fa-done">
                Готово
              </Button>
            </div>
          </div>
        )}

        {/* DISABLE */}
        {step === 'disabling' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">Для отключения 2FA введите пароль и текущий код из приложения:</p>
            <div>
              <Label>Пароль</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="2fa-disable-pwd" />
            </div>
            <div>
              <Label>Код из приложения или код восстановления</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} maxLength={20} data-testid="2fa-disable-code" />
            </div>
            <div className="flex gap-2">
              <Button variant="destructive" onClick={disable2FA} disabled={!password || !code.trim()} data-testid="2fa-disable-confirm">
                Отключить 2FA
              </Button>
              <Button variant="outline" onClick={() => { setStep('idle'); setPassword(''); setCode(''); }}>
                Отмена
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
